import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"

import { Box, Button, Switch, Typography } from "@mui/material"
import UIIcon from "../../components/UIIcon"
import {
  ContentContainer,
  TextButton,
  WhiteContainer,
  TitleContainer,
  SetButton,
  UnSetButton,
} from "./styled"
import { useBankSelect } from "./hooks/useBankSelect"
import BankIcon from "../../components/BankIcon"
import {
  resetSteps,
  setCurrentFlow,
  setCurrentStep,
  setNextStep,
} from "../../store/slices/routingSlice/routingSlice"
import { ROUTES_PATHS } from "../../utils/constants"
import { useSignAgreement } from "../../api/hooks/useSignAgreement"
import { useAgreement } from "../../api/hooks/useAgreement"
import { PHKOString } from "../../components/PHKOString"
import { PaymentInfoBlock } from "../../components/PaymentInfoBlock"
import DeleteBankPermissionModal from "../../components/Modal/DeleteBankPermissionModal"
import IconBox from "../../components/IconBox"
import { colors } from "../../theme/colors"
import { STEPS_NAME } from "../../store/slices/routingSlice/flowsSteps"
import SBPIconNoExt from "../../components/SBPIconNoExt"
import { WhiteCard } from "../../components/styled"

export const SettingsSBP = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { mutate } = useSignAgreement()
  const { data: agreement } = useAgreement()
  const { bankVariants } = useBankSelect()
  const [sbpAgreement, setSbpAgreement] = useState(!!agreement?.gaveAgreement || false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedBank, setSelectedBank] = useState({
    bankIcon: "",
    bankName: "",
    bankID: "",
  })

  const [showMoreBanks, setShowMoreBanks] = useState(false)

  const handleClickOpenModal = (
    bankIcon: string,
    bankName: string,
    bankID: string,
  ) => {
    setSelectedBank({ bankIcon, bankName, bankID })
    setOpenModal(!openModal)
  }

  const handleSbpAgreementChange = () => {
    const newValue = !sbpAgreement
    setSbpAgreement(newValue)
    mutate(newValue)
  }

  const handleBankTransfer = () => {
    dispatch(resetSteps())
    navigate(`/${ROUTES_PATHS.allowedBanks}`)
  }

  const handleSetClick = () => {
    dispatch(setNextStep(STEPS_NAME.sbpSettings.setDefaultBank))
  }

  const handleDeleteClick = () => {
    dispatch(setNextStep(STEPS_NAME.sbpSettings.deleteDefaultBank))
  }

  const handleShowMoreClick = () => {
    setShowMoreBanks(!showMoreBanks)
  }

  useEffect(() => {
    if (agreement) {
      setSbpAgreement(agreement.gaveAgreement)
    }
  }, [agreement])

  useEffect(() => {
    dispatch(setCurrentFlow("settingsSBP"))
    dispatch(setCurrentStep(STEPS_NAME.sbpSettings.sbpSettings))
  }, [])

  const banksToShow =
    bankVariants.length > 3 && !showMoreBanks
      ? bankVariants.slice(0, 4)
      : bankVariants
  const lastBanksLength = bankVariants.slice(3, -1).length

  const bankIcons = [
    ...(banksToShow?.map((bank) => (
      <BankIcon
        onClick={() =>
          handleClickOpenModal(bank.logoUrl, bank.title, bank.bankId)
        }
        key={bank.bankId}
        logoUrl={bank.logoUrl}
        size={30}
      />
    )) || []),
  ]

  const bankIconsWithHiddenBanksIcon =
    lastBanksLength > 0
      ? [
          ...bankIcons,
          <IconBox color="black" onClick={handleShowMoreClick}>
            <Typography color={colors.white}>+{lastBanksLength}</Typography>
          </IconBox>,
        ]
      : bankIcons

  const bankIcons3 = [
    ...(showMoreBanks ? bankIcons : bankIconsWithHiddenBanksIcon),
    <UIIcon
      key="add-new-bank-permission"
      onClick={handleBankTransfer}
      size={17}
      iconName="plusIcon"
    />,
  ]

  return (
    <Box sx={ContentContainer}>
      <SBPIconNoExt size={60} />
      <WhiteCard sx={WhiteContainer}>
        <Box sx={TitleContainer}>
          <Typography variant="h4Bold">Переводы по СБП</Typography>
          <Switch checked={sbpAgreement} onChange={handleSbpAgreementChange} />
        </Box>
        <Button variant="text" sx={TextButton}>
          Подробнее
        </Button>
      </WhiteCard>
      <PaymentInfoBlock
        margin="0px"
        title="Запрос перевода со своих счетов"
        description={{
          secondary: "Автоматические переводы себе по запросу из других банков",
        }}
        icons={bankIcons3}
      />
      <WhiteCard sx={WhiteContainer}>
        <Box sx={TitleContainer}>
          <Typography variant="h4Bold">
            Установить банк по умолчанию в СБП
          </Typography>
        </Box>
        <Typography variant="h7Pale">
          Установка РНКО «Единая касса» банком <br /> в СБП по умолчанию
        </Typography>
        <SetButton
          onClick={handleSetClick}
          disabled={!sbpAgreement}
          variant="contained"
          color="secondary"
        >
          <Typography variant="h4">Установить</Typography>
        </SetButton>
      </WhiteCard>
      <WhiteCard sx={WhiteContainer}>
        <Box sx={TitleContainer}>
          <Typography variant="h4Bold">
            Удаление банка по умолчанию в СБП
          </Typography>
        </Box>
        <UnSetButton
          onClick={handleDeleteClick}
          disabled={!sbpAgreement}
          variant="contained"
        >
          <Typography color="error" variant="h4">
            Удалить
          </Typography>
        </UnSetButton>
      </WhiteCard>
      <PHKOString />

      <DeleteBankPermissionModal
        open={openModal}
        onClose={() => setOpenModal(!openModal)}
        selectedBank={selectedBank}
      />
    </Box>
  )
}
